// client/src/AuthPage.jsx
import { useState } from "react";
import { api } from "./api.js";

const FONT = "'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', monospace";

function getInitialMode() {
  const params = new URLSearchParams(window.location.search);
  return params.get("reset_token") ? "reset" : "login";
}

function getResetTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("reset_token") || "";
}

export function AuthPage({ onLogin }) {
  const [mode, setMode] = useState(getInitialMode); // login | register | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken] = useState(getResetTokenFromUrl);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setSuccessMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    setLoading(true);

    try {
      if (mode === "register") {
        const data = await api.register(email, password, displayName);
        onLogin(data);
      } else if (mode === "login") {
        const data = await api.login(email, password);
        onLogin(data);
      } else if (mode === "forgot") {
        await api.forgotPassword(email);
        setSuccessMsg("Check your email for a reset link. It expires in 1 hour.");
      } else if (mode === "reset") {
        await api.resetPassword(resetToken, password);
        setSuccessMsg("Password updated! You can now sign in.");
        // Clear the token from URL without reload
        window.history.replaceState({}, "", window.location.pathname);
        setTimeout(() => switchMode("login"), 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: "100%", padding: "11px 14px", borderRadius: 8,
    border: "1px solid #e2e8f0", background: "#fff", color: "#1e293b",
    fontSize: 14, fontFamily: FONT, outline: "none", boxSizing: "border-box",
  };
  const label = { display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", fontFamily: MONO, letterSpacing: "0.5px", marginBottom: 5 };

  const titles = {
    login: { heading: "Sign In", sub: "Welcome back" },
    register: { heading: "Create Account", sub: "Get started today" },
    forgot: { heading: "Forgot Password", sub: "We'll send you a reset link" },
    reset: { heading: "Reset Password", sub: "Choose a new password" },
  };

  const { heading, sub } = titles[mode];

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6fb", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: 400, maxWidth: "100%" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800, color: "#fff", boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)", marginBottom: 16 }}>OC</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, fontFamily: FONT, color: "#1e293b", marginBottom: 6 }}>OrbitCal</h1>
          <p style={{ fontSize: 14, color: "#64748b" }}>Unified calendar visibility for your team</p>
        </div>

        {/* Auth Card */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 28, boxShadow: "0 4px 16px rgba(15, 23, 42, 0.06)" }}>
          {/* Tabs — only for login/register */}
          {(mode === "login" || mode === "register") && (
            <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
              {["login", "register"].map(m => (
                <button key={m} type="button" onClick={() => switchMode(m)}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 7, border: `1px solid ${mode === m ? "#2563eb" : "#e2e8f0"}`, background: mode === m ? "#eff6ff" : "transparent", color: mode === m ? "#2563eb" : "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT, textTransform: "capitalize" }}>
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>
          )}

          {/* Forgot / Reset headings */}
          {(mode === "forgot" || mode === "reset") && (
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", fontFamily: FONT, margin: 0 }}>{heading}</h2>
              <p style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{sub}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {mode === "register" && (
              <div style={{ marginBottom: 14 }}>
                <label style={label}>YOUR NAME</label>
                <input style={inp} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. Justin Skelton" autoComplete="name" />
              </div>
            )}

            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <div style={{ marginBottom: 14 }}>
                <label style={label}>EMAIL</label>
                <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" />
              </div>
            )}

            {(mode === "login" || mode === "register" || mode === "reset") && (
              <div style={{ marginBottom: mode === "login" ? 8 : 20 }}>
                <label style={label}>PASSWORD</label>
                <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={mode === "register" || mode === "reset" ? "Min 8 characters" : "••••••••"} autoComplete={mode === "register" || mode === "reset" ? "new-password" : "current-password"} />
              </div>
            )}

            {/* Forgot password link — only on login */}
            {mode === "login" && (
              <div style={{ textAlign: "right", marginBottom: 20 }}>
                <button type="button" onClick={() => switchMode("forgot")} style={{ background: "none", border: "none", color: "#2563eb", fontSize: 12, cursor: "pointer", fontFamily: FONT, padding: 0 }}>
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#dc2626", marginBottom: 16 }}>{error}</div>
            )}

            {successMsg && (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#16a34a", marginBottom: 16 }}>{successMsg}</div>
            )}

            {!successMsg && (
              <button type="submit" disabled={loading}
                style={{ width: "100%", padding: "12px 0", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: FONT, boxShadow: "0 4px 16px rgba(59, 130, 246, 0.25)", opacity: loading ? 0.7 : 1 }}>
                {loading ? "..." : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Update Password"}
              </button>
            )}

            {/* Back to sign in */}
            {(mode === "forgot" || mode === "reset") && (
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <button type="button" onClick={() => switchMode("login")} style={{ background: "none", border: "none", color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: FONT }}>
                  &larr; Back to Sign In
                </button>
              </div>
            )}
          </form>
        </div>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: "#94a3b8" }}>
          Calendars synced via ICS feeds. Works with Outlook 365, Outlook Legacy, Gmail, and more.
        </p>
      </div>
    </div>
  );
}
